import root from 'window-or-global';
import { storageAvailable } from 'utils/storage-available';
import { saveCookie, getCookie } from 'utils/cookie';
import { dayjs } from 'utils/date';

type StorageType = 'localStorage' | 'sessionStorage';

const getExpiry = (type: StorageType) =>
  type === 'sessionStorage' ? null : dayjs().add(5, 'year').startOf('day').toDate();

class StoragePolyfill {
  type: StorageType;

  cookieName: `${StorageType}PolyFill`;

  constructor(type: StorageType) {
    this.type = type;
    this.cookieName = `${type}PolyFill`;
    this.setData({});
  }

  getData(): Record<string, string> {
    const serialisedData = getCookie(this.cookieName);

    return serialisedData ? JSON.parse(decodeURIComponent(serialisedData)) : {};
  }

  setData(data: Record<string, string>): void {
    const expiry = getExpiry(this.type);
    const serialisedData = encodeURIComponent(JSON.stringify(data));

    saveCookie(this.cookieName, serialisedData, expiry);
  }

  get length() {
    const data = this.getData();

    return Object.keys(data).length;
  }

  clear() {
    this.setData({});
  }

  getItem(key: string) {
    const data = this.getData();

    return data[key] ?? null;
  }

  key(index: number) {
    const data = this.getData();
    const key = Object.keys(data)[index];

    return key === undefined ? null : key;
  }

  removeItem(key: string) {
    const data = this.getData();
    delete data[key];
    this.setData(data);
  }

  setItem(key: string, value: unknown) {
    const data = this.getData();
    data[key] = `${value}`;
    this.setData(data);
  }
}

/**
 * Creates a storage instance.
 *
 * If the storage type is natively available then it is used.
 *
 * If the storage type is not available, then a polyfilled store is created
 * backed by a cookie. Client-side the cookie is stored using browser cookie
 * storage. Server-side the cookie is store using async local storage if
 * available. See `src/utils/cookie.ts` for further implementation details.
 *
 * **Note:** All server-side use must originate from the frontend controller in
 * `src/server/controllers/frontend/index.ts` otherwise the storage will not be
 * persisted.
 *
 * **Note:** Server-side storage set using this method is not currently sent to
 * the client. This **cannot** be relied upon as a server -> client state
 * transfer mechanism.
 *
 * @param type The type of storage, one of: 'localStorage', 'sessionStorage'.
 * @returns Native or polyfilled storage instance.
 */
export function createStorage(type: StorageType) {
  if (storageAvailable(type)) {
    return root[type];
  }

  return new StoragePolyfill(type);
}
